import { Injectable } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { ArticleService } from './article.service';
import {API_TYPE, HttpHelperService} from "../../../services/http-helper.service";
import {
  BenefitDetailsPopupComponent
} from "../components/membership-benefits/benefit-details-popup/benefit-details-popup.component";
import {Modal, overlayConfigFactory} from 'src/app/modules/shared/modal/ngx-modal-compat';
import {BSModalContext} from 'src/app/modules/shared/modal/ngx-modal-compat';
import {
  CommonService,
  MEMBERSHIP_SESSION_KEY_PREFIX,
  SESS_KEY_ADMIN_SEARCH_PERSON_MEMBERSHIPS,
  SESS_KEY_ADMIN_SEARCH_PERSON_POA_MEMBERSHIPS,
  SESSION_KEY_AUTH_MEMBERSHIPS,
  SESSION_KEY_AUTH_POA_MEMBERSHIPS
} from "../../shared/services/common.service";

@Injectable({
  providedIn: 'root'
})
export class MemberService {
  API_TYPE = API_TYPE;

  /**
   * One membership page view asked the API for the same membership two or three
   * times: the overview constructor, the Bonus tab and a second constructor run
   * on the list redirect each made their own call, and the benefits were
   * fetched twice the same way. Each call takes 1 to 3 s on the API, so the
   * response is shared instead. The memberships and fullmakt lists are shared
   * the same way: the landing sequence and the membership page's cold-link
   * fallback each asked for them separately (FIND-10, WO-28).
   *
   * The entry is the request, not the payload, so callers that arrive while the
   * first call is still open wait on it rather than starting a second. Every
   * mutation below drops the whole store, so a changed bank account, a deleted
   * card or an ordered payout is read back from the API and not from memory. A
   * failed call is dropped as well, so a page can recover by asking again.
   *
   * One store, because the key carries what it is a request for. A membership
   * key names the company too: the members' list hard-codes BORI on every link
   * and the administration page passes the membership's own company, and
   * whether the API's membership ids are unique across companies is not visible
   * from this code. The company in the key is the one the interceptor will send
   * with the request, so the two cannot disagree. (WO-28)
   */
  private requests = new Map<string, CachedRequest>();

  /** Overridable so a spec can move the clock instead of waiting a day. */
  now: () => number = () => Date.now();

  /** Emits when the member asks for fresh data, so open pages reload theirs. */
  readonly refreshed$ = new Subject<void>();

  constructor(
    private modal: Modal,
    private httpHelper: HttpHelperService,
    private commonService: CommonService,
    private articleService: ArticleService
  ) { }

  getMembershipBenefits(membershipId: any, companyId?: any) {
    // https://openapi.styret.com/membership/GetMembershipBenefitsById?membershipId=11
    return this.shared('benefits:' + this.companyKey(companyId) + ':' + membershipId, () =>
      this.httpHelper.get('GetMembershipBenefitsById', {membershipId}, API_TYPE.MEMBER_API));
  }

  /**
   * The company the interceptor will put on the request: the one a membership
   * page stored for this view, or BORI, which is what the interceptor falls
   * back to (`httpinterceptor.ts`).
   *
   * The argument keys the entry; it does not travel with the request, which
   * carries whatever `setCompanyForAPIHeader` last stored. A caller therefore
   * passes the company it has already stored for this view, as both membership
   * components do from the same route parameter. Passing a different one would
   * key an answer under a company the request never used.
   */
  private companyKey(companyId?: any): string {
    if (companyId !== undefined && companyId !== null && String(companyId) !== '') {
      return String(companyId);
    }
    return String(this.articleService.getCompanyForAPIHeader() || BORI_BBL_COMP_ID);
  }

  private shared(key: string, request: () => Observable<any>): Observable<any> {
    const existing = this.requests.get(key);
    if (existing && this.now() - existing.fetchedAt < MEMBERSHIP_CACHE_MAX_AGE_MS) {
      return existing.request;
    }
    this.requests.delete(key);
    let pending: Observable<any>;
    pending = request().pipe(
      shareReplay({ bufferSize: 1, refCount: false }),
      catchError((error: any) => {
        // Only when this is still the entry under that key. A refresh while the
        // call was open has already put a new one there, and deleting by key
        // alone would throw away the answer that replaced this one.
        if (this.requests.get(key)?.request === pending) {
          this.requests.delete(key);
        }
        return throwError(() => error);
      })
    );
    this.requests.set(key, { request: pending, fetchedAt: this.now() });
    return pending;
  }

  /** Drops every shared response. Called after a mutation and on logout. */
  clearMembershipCache(): void {
    this.requests.clear();
  }

  /**
   * The member asked for fresh figures. One press refreshes the page rather
   * than one tab: the bonus figure, the cards and the payout account repeat
   * under every membership, because a person has one bonus number however many
   * memberships they hold. (WO-28)
   */
  refresh(): void {
    this.clearMembershipCache();
    this.refreshed$.next();
  }


  /** Stamped for the live account, like the APP_AUTH_* entries. */
  storeMembershipData(membershipId: any, data: any){
    const sessionKey = MEMBERSHIP_SESSION_KEY_PREFIX + membershipId;
    const jsonStr = JSON.stringify(data);
    this.commonService.setOwnedSessionItem(sessionKey, jsonStr);
  }

  /** Served only to the account the session was written for, like the APP_AUTH_* entries. */
  getMembershipData(membershipId: any){
    const sessionKey = MEMBERSHIP_SESSION_KEY_PREFIX + membershipId;
    let data: any = null;
    const sessionData = this.commonService.getOwnedSessionItem(sessionKey);
    if(sessionData){
      data = JSON.parse(sessionData)
    }
    return data;
  }

  GetMembershipById(membershipId: any, companyId?: any) {
    return this.shared('membership:' + this.companyKey(companyId) + ':' + membershipId, () =>
      this.httpHelper.get('GetMembershipById', {membershipId}, API_TYPE.MEMBER_API));
  }

  deleteCard(externalCardId: any, membershipId: any) {
    const params = {externalCardId,membershipId};
    return this.httpHelper.delete('DeleteCard', params,API_TYPE.MEMBER_API)
      .pipe(tap(() => this.clearMembershipCache()));
  }

  OrderCashback(obj: any) {
    return this.httpHelper.post('OrderCashback', obj,API_TYPE.MEMBER_API)
      .pipe(tap(() => this.clearMembershipCache()));
  }

  UpdateMemberAccountNumber(obj: any) {
    return this.httpHelper.post('UpdateMemberAccountNumber', obj,API_TYPE.MEMBER_API)
      .pipe(tap(() => this.clearMembershipCache()));
  }

  updateMemberAccountNumbersForUser(obj: { accountNumber: string }) {
    return this.httpHelper.post('UpdateMemberAccountNumbersForUser', obj, API_TYPE.MEMBER_API)
      .pipe(tap(() => this.clearMembershipCache()));
  }

  deleteMemberAccountNumber(membershipId: any) {
    const params = {membershipId};
    return this.httpHelper.delete('DeleteMemberAccountNumber', params,API_TYPE.MEMBER_API)
      .pipe(tap(() => this.clearMembershipCache()));
  }

  generateAddCardIframe(cardType: any, membershipId: any) {
    const params = {cardType,membershipId};
    return this.httpHelper.postTextContentType('GenerateAddCardIframe?cardType='+ cardType +'&membershipId=' + membershipId);
  }

  getMemberships() {
    return this.httpHelper.get('GetMemberships', '',API_TYPE.MEMBER_API);
  }

  getPoAMemberships(personNo?: any) {
    let params: any = {};
    if(personNo){
      params.personNumber = personNo;
    }
    return this.httpHelper.get('GetPoAMemberships', params,API_TYPE.MEMBER_API);
  }

  getMembershipsByPersonNumber(personNumber: any) {
    return this.httpHelper.get('GetMembershipsByPersonNumber', {personNumber},API_TYPE.MEMBER_API);
  }

  openBenefitDetailsPopup(data: any){
    const dialog = this.modal.open(BenefitDetailsPopupComponent, overlayConfigFactory({
      dialogClass: 'modal-dialog modal-1300',
      data
    }, BSModalContext));
    dialog.result.then((result: any) => {
    });
  }

  generateVerifyCardIframe(externalCardId: any, membershipId: any) {
    return this.httpHelper.postTextContentType('GenerateVerifyCardIframe?externalCardId='+ externalCardId +'&membershipId=' + membershipId);
  }

  //-------- MANAGING MEMBERSHIPS FOR ADMIN SEARCH PERSON + RELATED POA PERSON
  /** An empty answer clears the stored list; a missing one leaves it alone. */
  setMembershipsData(type: any, data: any) {
    let sessKeyMemberData = SESS_KEY_AUTH_MEMBERSHIPS;
    if(type == MEMB_DATA_TYPE.ADMIN_SRCH_PERSON){
      sessKeyMemberData = SESS_KEY_ADMIN_SEARCH_PERSON_MEMBERSHIPS;
    }

    if (Array.isArray(data) && data.length) {
      sessionStorage.setItem(
        sessKeyMemberData,
        JSON.stringify(data)
      );
    } else if (Array.isArray(data)) {
      sessionStorage.removeItem(sessKeyMemberData);
    }
  }
  // --------
  /** An empty answer clears the stored list; a missing one leaves it alone. */
  setPoaMembershipsData(type: any, data: any) {
    let sessKeyPoaMemberData = SESS_KEY_AUTH_POA_MEMBERSHIPS;
    if(type == MEMB_DATA_TYPE.ADMIN_SRCH_PERSON){
      sessKeyPoaMemberData = SESS_KEY_ADMIN_SEARCH_PERSON_POA_MEMBERSHIPS;
    }

    if (Array.isArray(data) && data.length) {
      sessionStorage.setItem(
        sessKeyPoaMemberData,
        JSON.stringify(data)
      );
    } else if (Array.isArray(data)) {
      sessionStorage.removeItem(sessKeyPoaMemberData);
    }
  }
  // --------
  /** Drops the own and fullmakt lists, so a new search cannot show the previous person's. */
  clearMembershipsData(type: any) {
    if(type == MEMB_DATA_TYPE.ADMIN_SRCH_PERSON){
      sessionStorage.removeItem(SESS_KEY_ADMIN_SEARCH_PERSON_MEMBERSHIPS);
      sessionStorage.removeItem(SESS_KEY_ADMIN_SEARCH_PERSON_POA_MEMBERSHIPS);
    } else {
      sessionStorage.removeItem(SESS_KEY_AUTH_MEMBERSHIPS);
      sessionStorage.removeItem(SESS_KEY_AUTH_POA_MEMBERSHIPS);
    }
  }
  // --------
  getMembershipsData(type: any) {
    let sessKeyMemberData = SESS_KEY_AUTH_MEMBERSHIPS;
    let sessKeyPoaMemberData = SESS_KEY_AUTH_POA_MEMBERSHIPS;
    if(type == MEMB_DATA_TYPE.ADMIN_SRCH_PERSON){
      sessKeyMemberData = SESS_KEY_ADMIN_SEARCH_PERSON_MEMBERSHIPS;
      sessKeyPoaMemberData = SESS_KEY_ADMIN_SEARCH_PERSON_POA_MEMBERSHIPS;
    }
    let parsedAuthData: any = [];
    let parsedAuthPoaData: any = [];
    const authData = sessionStorage.getItem(sessKeyMemberData);
    const authPoaData = sessionStorage.getItem(sessKeyPoaMemberData);
    if (authData) {
      parsedAuthData = JSON.parse(authData);
    }
    if (authPoaData) {
      parsedAuthPoaData = JSON.parse(authPoaData);
      parsedAuthPoaData.forEach((item: any) => {
        item.isPoaItem = true;
        parsedAuthData.push(item);
      })
    }
    const activeMemberss: any = [];
    parsedAuthData.forEach((item: any) => {
      if(item.membershipTypeInfo){
        if(item.membershipTypeInfo.code != 'U'){
          activeMemberss.push(item);
        }
      }else{ // ALLOWING OTHER MEMBERSHIP RECORDS WITHOUT TYPE INFO JUST IN CASE POA API DOESNT HAVE
        activeMemberss.push(item);
      }
    })
    return activeMemberss.length ? activeMemberss : null ;
  }
  // -------
}


export const BORI_BBL_COMP_ID = '894';

/**
 * How long a shared answer is replayed before the API is asked again: the two
 * per-membership calls and the two list calls alike. A day, because the figures
 * change rarely, the call costs 1 to 2 s, and a member who wants the current one
 * has the refresh control. (WO-28)
 */
export const MEMBERSHIP_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

interface CachedRequest {
  request: Observable<any>;
  fetchedAt: number;
}

export const MEMB_DATA_TYPE = {
  ADMIN_SRCH_PERSON: 'ADMIN_SRCH_PERSON',
  AUTH_USER: 'AUTH_USER'
}

export const SESS_KEY_AUTH_POA_MEMBERSHIPS = 'SESS_KEY_AUTH_POA_MEMBERSHIPS';
export const SESS_KEY_AUTH_MEMBERSHIPS = 'SESS_KEY_AUTH_MEMBERSHIPS';
